<script lang="ts">
  import { environment } from "$lib/utils/env";
  import { push } from "svelte-spa-router";

  import { LoadingSpinner } from "$ui/loading-spinner";
  import { Button } from "$ui/button";

  import Logo from "$assets/icons/logo.svelte";
  import GoogleLogo from "$assets/icons/google-logo.svelte";
  import MicrosoftLogo from "$assets/icons/microsoft-logo.svelte";
  import { onMount } from "svelte";
  import { firebaseApp } from "$lib/utils/env";
  import {
    getAuth,
    GoogleAuthProvider,
    isSignInWithEmailLink,
    OAuthProvider,
    signInWithEmailLink,
    signInWithPopup,
    signInWithRedirect,
    type AuthProvider,
  } from "firebase/auth";
  import { authStateStore } from "$lib/stores/user";
  import FullPageShell from "$lib/components/layout/FullPageShell.svelte";
  import { Input } from "$ui/input";
  import * as yup from "yup";
  import { Form } from "$lib/ui/form";
  import { makeApiRequest } from "$lib/utils/api";

  let isLoading = true;
  let errorMessage: string | null;

  let showSignInEmailLinkSent = false;
  let emailAddressValue: string | null;
  let supportedProviders = ["google.com", "microsoft.com", "email"];

  const storageKeyEmail = "emailForSignIn";
  const storageKeyCredentials = "pendingCred";

  function onError(error: Error) {
    console.error("Error signing in:", error);
    isLoading = false;
    errorMessage = error.message;
  }

  let form: any;
  let errors: any;
  let validateField: any;
  let updateValidateField: any;

  let initialValues = {
    email: undefined,
  };

  const validationSchema = yup.object().shape({
    email: yup.string().email("Invalid email format").required("Email is required"),
  });

  const onSubmit = async (values: any) => {
    isLoading = true;
    const email = values.email;
    window.localStorage.setItem(storageKeyEmail, email);
    try {
      await makeApiRequest(`/sign-in-with-email`, { email }, "POST");
      emailAddressValue = email;
      showSignInEmailLinkSent = true;
    } catch (err) {
      onError(err);
    } finally {
      isLoading = false;
    }
  };

  onMount(async () => {
    authStateStore.subscribe(async (authState) => {
      if (!authState.loading && authState.userInfo) {
        // Clean up query params
        history.replaceState(null, "", window.location.pathname);
        await push("/");
        return;
      }
    });
    const emailLink = (await isSignInWithEmailLink(getAuth(await firebaseApp), window.location.href))
      ? window.location.href
      : null;
    if (emailLink) {
      try {
        const storedEmail = window.localStorage.getItem(storageKeyEmail);
        if (!storedEmail) {
          return false;
        }

        // This browser has logged in previously, and is now connecting a new auth provider via email link
        await signInWithEmailLink(getAuth(await firebaseApp), storedEmail, emailLink);
      } catch (error: any) {
        onError(error as Error);
      }
    }

    isLoading = false;
  });

  async function signInWithProvider(providerName: "google" | "microsoft") {
    isLoading = true;
    errorMessage = null;
    let provider: AuthProvider;
    if (providerName === "google") {
      provider = new GoogleAuthProvider();
    } else if (providerName === "microsoft") {
      provider = new OAuthProvider("microsoft.com");
    } else {
      throw new Error(`Unknown provider ${providerName}`);
    }

    const auth = getAuth(await firebaseApp);
    try {
      if (auth.config.authDomain?.endsWith(".firebaseapp.com")) {
        await signInWithPopup(auth, provider);
      } else {
        signInWithRedirect(auth, provider);
      }
    } catch (error: any) {
      if (error.code === "auth/account-exists-with-different-credential") {
        onError(Error("You signed in with other login method."));
      } else {
        onError(error as Error);
      }
    }
  }

  const date = new Date();
  const year = date.getFullYear();
</script>

<FullPageShell class="flex flex-col">
  <nav class="flex">
    <div class="flex justify-between items-center gap-4 p-5 max-w-7xl mx-auto">
      <div class="[&>svg]:h-9">
        <Logo />
      </div>
    </div>
  </nav>
  <main class="flex-[2.5] flex justify-center items-center">
    <div class="flex flex-col items-center gap-8 w-full max-w-[360px]">
      <div class="flex flex-col items-center gap-4">
        <div class="flex flex-col items-center gap-2">
          <h1 class="text-3xl font-semibold">Sign In</h1>
        </div>
      </div>
      <div class="sm:min-w-[360px] min-h-[120px] relative flex">
        {#if isLoading}
          <div class="flex-1 flex justify-center items-center">
            <LoadingSpinner />
          </div>
        {:else if showSignInEmailLinkSent}
          <div class="flex-1 flex justify-center items-center text-center">
            An email has been sent to your inbox<br />
            with a link to complete your sign-in.
            <br /><br />
            {emailAddressValue}
          </div>
        {:else}
          <div class="w-full flex flex-col gap-3">
            {#if supportedProviders.includes("google.com")}
              <Button
                variant="outline"
                size="lg"
                class="bg-white dark:bg-transparent [&>svg]:h-[22px] flex gap-3"
                on:click={() => signInWithProvider("google")}
              >
                <GoogleLogo />
                Sign in with Google</Button
              >
            {/if}
            {#if supportedProviders.includes("microsoft.com")}
              <Button
                variant="outline"
                size="lg"
                on:click={() => signInWithProvider("microsoft")}
                class="bg-white dark:bg-transparent [&>svg]:h-[22px] flex gap-3"
              >
                <MicrosoftLogo />
                Sign in with Microsoft</Button
              >
            {/if}

            {#if supportedProviders.includes("email")}
              <p class="w-full text-center uppercase font-bold">or</p>
              <Form
                bind:form
                {initialValues}
                {validationSchema}
                {onSubmit}
                bind:errors
                bind:validateField
                bind:updateValidateField
                submitButtonText="Sign in"
                submittingButtonText="Signing in"
              >
                {#if $form}
                  <Input
                    name="email"
                    placeholder="e.g. user@recordpoint.com"
                    bind:value={$form.email}
                    on:input={() => {
                      validateField("email");
                    }}
                    error={$errors.email}
                  />
                {/if}
              </Form>
            {/if}
          </div>
        {/if}
      </div>

      <div class="text-center text-sm lg:px-12">
        {#await environment}
          <p>loading...</p>
        {:then env}
          By signing in you agree to our
          <a
            href={env.legals.termsOfService}
            target="_blank"
            rel="noreferrer"
            class="text-primary dark:text-primary-700 underline">Terms of Service</a
          >
          and
          <a
            href={env.legals.privacyPolicy}
            target="_blank"
            rel="noreferrer"
            class="text-primary dark:text-primary-700 underline">Privacy Policy</a
          >
        {/await}
      </div>
      {#if errorMessage}
        <div class="text-danger-400 text-sm text-center">{errorMessage}</div>
      {/if}
    </div>
  </main>
  <footer class="flex-1 flex items-end">
    <div class="flex justify-center items-center gap-4 p-8 max-w-7xl mx-auto">
      <div class="flex items-center gap-4">
        <p class="text-sm text-center">© RecordPoint {year}. All rights reserved.</p>
      </div>
    </div>
  </footer>
</FullPageShell>
